import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { CaretDown, Wrench, Plus, PencilSimple, Trash, X, ArrowUp, ArrowDown, Upload as UploadIcon, Star } from "@phosphor-icons/react";

const STATUSES = ["pending", "confirmed", "active", "completed", "cancelled"];
const AVAIL = [
  { key: "available", label: "Available", cls: "bg-green-100 text-green-800" },
  { key: "reserved", label: "Reserved", cls: "bg-blue-100 text-blue-800" },
  { key: "maintenance", label: "Under Service", cls: "bg-red-100 text-red-700" },
];
const STATUS_COLOR = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-blue-100 text-blue-800",
  active: "bg-green-100 text-green-800",
  completed: "bg-neutral-200 text-neutral-700",
  cancelled: "bg-red-100 text-red-700",
};
const CATEGORIES = ["mountain", "road", "gravel", "trekking", "city", "kids", "e-mtb", "e-trekking", "e-city"];
const PRICE_KEYS = ["hourly", "half-day", "full-day", "weekend", "weekly", "monthly"];

// Empty template for the "Add new" form. Every field mirrors the backend
// RentalBikeCreateIn defaults so we never post partial data.
const emptyBike = () => ({
  name: "", brand: "", category: "mountain",
  short_description: "", description: "",
  images: [],
  pricing: { hourly: 0, "half-day": 0, "full-day": 0, weekend: 0, weekly: 0, monthly: 0 },
  price_unit: "full-day",
  deposit: 150,
  sizes: [],
  specifications: {},
  availability: "available",
  active: true, featured: false,
});

export default function AdminRentals() {
  const [tab, setTab] = useState("bookings"); // bookings | tours | fleet
  const [bookings, setBookings] = useState([]);
  const [tourBookings, setTourBookings] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | bikeId
  const [form, setForm] = useState(emptyBike());
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/rental-bookings").then((r) => setBookings(r.data)),
      api.get("/admin/tour-bookings").then((r) => setTourBookings(r.data)),
      api.get("/admin/rental-bikes").then((r) => setFleet(r.data)),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateStatus = async (endpoint, b, status) => {
    try {
      await api.patch(`/admin/${endpoint}/${b.id}`, { status });
      toast.success(`${b.booking_number} → ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const patchBike = async (bike, patch) => {
    try {
      await api.patch(`/admin/rental-bikes/${bike.id}`, patch);
      toast.success(`${bike.name} updated`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const openNew = () => { setForm(emptyBike()); setEditing("new"); };
  const openEdit = (bike) => {
    setForm({
      ...emptyBike(), ...bike,
      pricing: { ...emptyBike().pricing, ...(bike.pricing || {}) },
      sizes: bike.sizes || [], images: bike.images || [], specifications: bike.specifications || {},
    });
    setEditing(bike.id);
  };
  const closeEditor = () => { setEditing(null); setForm(emptyBike()); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/rental-bikes", form);
        toast.success("Rental bike created");
      } else {
        await api.put(`/admin/rental-bikes/${editing}`, form);
        toast.success("Rental bike updated");
      }
      closeEditor();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (bike) => {
    if (!window.confirm(`Delete "${bike.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/rental-bikes/${bike.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const move = async (bike, direction) => {
    const idx = fleet.findIndex((b) => b.id === bike.id);
    const nextIdx = direction === "up" ? idx - 1 : idx + 1;
    if (nextIdx < 0 || nextIdx >= fleet.length) return;
    const newOrder = [...fleet];
    [newOrder[idx], newOrder[nextIdx]] = [newOrder[nextIdx], newOrder[idx]];
    setFleet(newOrder);
    try {
      await api.post("/admin/rental-bikes/reorder", { ids: newOrder.map((b) => b.id) });
    } catch { toast.error("Reorder failed"); load(); }
  };

  const TAB_CLS = (k) => `px-4 py-2 text-xs uppercase font-bold tracking-widest border-b-2 transition-colors ${tab === k ? "border-[color:var(--vt-orange)] text-black" : "border-transparent text-neutral-500 hover:text-black"}`;

  return (
    <div className="p-8 lg:p-12" data-testid="admin-rentals">
      <div className="mb-6">
        <div className="overline text-[color:var(--vt-orange)] mb-2">/ FLEET & RENTALS</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Rental Management</h1>
      </div>

      <div className="flex gap-1 border-b border-neutral-200 mb-6">
        <button onClick={() => setTab("bookings")} className={TAB_CLS("bookings")} data-testid="tab-bookings">Rental Bookings ({bookings.length})</button>
        <button onClick={() => setTab("tours")} className={TAB_CLS("tours")} data-testid="tab-tours">Tour Bookings ({tourBookings.length})</button>
        <button onClick={() => setTab("fleet")} className={TAB_CLS("fleet")} data-testid="tab-fleet">Fleet ({fleet.length})</button>
      </div>

      {loading && <div className="p-12 text-center text-neutral-500">Loading…</div>}

      {!loading && tab === "bookings" && (
        <div className="bg-white border border-neutral-200">
          {bookings.length === 0 ? <div className="p-12 text-center text-neutral-500">No bookings yet.</div> :
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left p-3 overline text-neutral-600">Booking</th>
                <th className="text-left p-3 overline text-neutral-600">Bike</th>
                <th className="text-left p-3 overline text-neutral-600">Customer</th>
                <th className="text-left p-3 overline text-neutral-600">Dates</th>
                <th className="text-right p-3 overline text-neutral-600">Total</th>
                <th className="text-left p-3 overline text-neutral-600">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <React.Fragment key={b.id}>
                  <tr className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer" onClick={() => setExpanded(expanded === b.id ? null : b.id)} data-testid={`rental-row-${b.booking_number}`}>
                    <td className="p-3 font-bold">#{b.booking_number}</td>
                    <td className="p-3">{b.bike_name}</td>
                    <td className="p-3">
                      <div>{b.customer_name}</div>
                      <div className="text-[10px] text-neutral-500">{b.customer_email}</div>
                    </td>
                    <td className="p-3 text-xs">
                      <div>{b.start_date?.slice(0, 10)}</div>
                      <div>→ {b.end_date?.slice(0, 10)}</div>
                    </td>
                    <td className="p-3 text-right font-bold">€{b.total?.toFixed(2)}</td>
                    <td className="p-3">
                      <select className={`text-xs px-2 py-1 font-bold uppercase ${STATUS_COLOR[b.status] || ""}`}
                        value={b.status}
                        onChange={(e) => { e.stopPropagation(); updateStatus("rental-bookings", b, e.target.value); }}
                        onClick={(e) => e.stopPropagation()}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="p-3"><CaretDown size={14} className={`transition-transform ${expanded === b.id ? "rotate-180" : ""}`} /></td>
                  </tr>
                  {expanded === b.id && (
                    <tr className="bg-neutral-50">
                      <td colSpan="7" className="p-4 text-xs">
                        <div className="grid md:grid-cols-3 gap-4">
                          <div><div className="overline text-neutral-500 mb-1">Duration</div>{b.duration_type}</div>
                          <div><div className="overline text-neutral-500 mb-1">Pickup</div>{b.pickup_location}</div>
                          <div><div className="overline text-neutral-500 mb-1">Phone</div>{b.customer_phone}</div>
                          <div><div className="overline text-neutral-500 mb-1">Accessories</div>{(b.accessories || []).join(", ") || "—"}</div>
                          <div><div className="overline text-neutral-500 mb-1">Insurance</div>{b.insurance ? "Yes" : "No"}</div>
                          <div><div className="overline text-neutral-500 mb-1">Agreement</div>{b.agreement_accepted ? "Accepted" : "—"}</div>
                          {b.notes && <div className="col-span-3"><div className="overline text-neutral-500 mb-1">Notes</div>{b.notes}</div>}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>}
        </div>
      )}

      {!loading && tab === "tours" && (
        <div className="bg-white border border-neutral-200" data-testid="tour-bookings-table">
          {tourBookings.length === 0 ? <div className="p-12 text-center text-neutral-500">No tour bookings yet.</div> :
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left p-3 overline text-neutral-600">Booking</th>
                <th className="text-left p-3 overline text-neutral-600">Tour</th>
                <th className="text-left p-3 overline text-neutral-600">Customer</th>
                <th className="text-left p-3 overline text-neutral-600">Date</th>
                <th className="text-center p-3 overline text-neutral-600">Pax</th>
                <th className="text-right p-3 overline text-neutral-600">Total</th>
                <th className="text-left p-3 overline text-neutral-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {tourBookings.map((b) => (
                <tr key={b.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`tour-row-${b.booking_number}`}>
                  <td className="p-3 font-bold">#{b.booking_number}</td>
                  <td className="p-3">
                    <div>{b.tour_name}</div>
                    <div className="text-[10px] text-neutral-500 uppercase">{b.tour_type}</div>
                  </td>
                  <td className="p-3">
                    <div>{b.customer_name}</div>
                    <div className="text-[10px] text-neutral-500">{b.customer_email}</div>
                    <div className="text-[10px] text-neutral-500">{b.customer_phone}</div>
                  </td>
                  <td className="p-3 text-xs">{b.date?.slice(0, 10)}</td>
                  <td className="p-3 text-center">{b.participants}</td>
                  <td className="p-3 text-right font-bold">€{b.total?.toFixed(2)}</td>
                  <td className="p-3">
                    <select className={`text-xs px-2 py-1 font-bold uppercase ${STATUS_COLOR[b.status] || ""}`}
                      value={b.status}
                      onChange={(e) => updateStatus("tour-bookings", b, e.target.value)}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>}
        </div>
      )}

      {!loading && tab === "fleet" && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-neutral-500">{fleet.length} bike{fleet.length === 1 ? "" : "s"} in fleet · use ↑↓ to reorder · inactive bikes are hidden from the public rental page</p>
            <button onClick={openNew} className="vt-btn-orange text-sm" data-testid="add-rental-bike-btn">
              <Plus size={16} /> Add rental bike
            </button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fleet-grid">
            {fleet.map((bike, idx) => {
              const meta = AVAIL.find((a) => a.key === bike.availability) || AVAIL[0];
              return (
                <div key={bike.id} className={`border bg-white overflow-hidden ${bike.active === false ? "opacity-60 border-neutral-300" : "border-neutral-200"}`} data-testid={`fleet-bike-${bike.id}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-neutral-100 relative">
                    {bike.images?.[0] ? <img src={bike.images[0]} alt={bike.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">No image</div>}
                    <span className={`absolute top-3 left-3 text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${meta.cls}`}>{meta.label}</span>
                    {bike.active === false && <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-neutral-800 text-white">Inactive</span>}
                    {bike.featured && bike.active !== false && <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[color:var(--vt-orange)] text-black flex items-center gap-1"><Star size={10} weight="fill" /> Featured</span>}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="overline text-neutral-500">{bike.brand} · {bike.category}</div>
                      <div className="flex gap-1">
                        <button onClick={() => move(bike, "up")} disabled={idx === 0} className="p-1 border border-neutral-300 hover:border-black disabled:opacity-30" title="Move up" data-testid={`move-up-${bike.id}`}><ArrowUp size={12} /></button>
                        <button onClick={() => move(bike, "down")} disabled={idx === fleet.length - 1} className="p-1 border border-neutral-300 hover:border-black disabled:opacity-30" title="Move down" data-testid={`move-down-${bike.id}`}><ArrowDown size={12} /></button>
                      </div>
                    </div>
                    <h3 className="font-display font-bold text-base mb-2">{bike.name}</h3>
                    <div className="text-xs text-neutral-500 mb-3">Full-day: €{bike.pricing?.["full-day"]} · Deposit: €{bike.deposit ?? 150}</div>
                    <label className="overline text-neutral-600 block mb-1">Availability</label>
                    <select className={`w-full text-xs px-2 py-2 font-bold uppercase mb-3 ${meta.cls}`}
                      value={bike.availability || "available"}
                      onChange={(e) => patchBike(bike, { availability: e.target.value })}
                      data-testid={`fleet-status-${bike.id}`}>
                      {AVAIL.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                    </select>
                    <div className="flex items-center gap-2 mb-3 text-xs">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={bike.active !== false} onChange={(e) => patchBike(bike, { active: e.target.checked })} data-testid={`active-${bike.id}`} />
                        Active
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={!!bike.featured} onChange={(e) => patchBike(bike, { featured: e.target.checked })} data-testid={`featured-${bike.id}`} />
                        Featured
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(bike)} className="flex-1 border border-black text-xs font-bold uppercase px-3 py-2 hover:bg-black hover:text-white transition-colors" data-testid={`edit-${bike.id}`}>
                        <PencilSimple size={12} className="inline mr-1" /> Edit
                      </button>
                      <button onClick={() => remove(bike)} className="border border-red-300 text-red-600 text-xs font-bold uppercase px-3 py-2 hover:bg-red-600 hover:text-white transition-colors" data-testid={`delete-${bike.id}`}>
                        <Trash size={12} />
                      </button>
                    </div>
                    {bike.availability === "maintenance" && (
                      <div className="mt-2 text-[11px] text-red-700 flex items-center gap-1"><Wrench size={12} weight="fill" /> Hidden from public rental page</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {editing && (
        <BikeEditor
          form={form} setForm={setForm}
          onCancel={closeEditor} onSubmit={submit}
          saving={saving} isNew={editing === "new"}
        />
      )}
    </div>
  );
}

// ------- Editor modal -------
function BikeEditor({ form, setForm, onCancel, onSubmit, saving, isNew }) {
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });

  const uploadImage = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newUrls.push(data.url);
      }
      setForm((f) => ({ ...f, images: [...(f.images || []), ...newUrls] }));
      toast.success(`${newUrls.length} image${newUrls.length === 1 ? "" : "s"} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); if (fileInput.current) fileInput.current.value = ""; }
  };

  const addImageUrl = () => {
    const url = window.prompt("Paste an image URL:");
    if (url && url.trim()) setForm((f) => ({ ...f, images: [...(f.images || []), url.trim()] }));
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onCancel} data-testid="bike-editor-modal">
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="bg-white max-w-3xl w-full p-6 my-8 relative">
        <button type="button" onClick={onCancel} className="absolute top-3 right-3 p-2 hover:bg-neutral-100" data-testid="bike-editor-close"><X size={20} /></button>
        <div className="overline text-[color:var(--vt-orange)] mb-1">{isNew ? "New rental bike" : "Edit rental bike"}</div>
        <h2 className="font-display font-black text-2xl tracking-tighter mb-5">{form.name || "Untitled"}</h2>

        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <div className="md:col-span-2">
            <label className="overline text-neutral-600 block mb-1">Name *</label>
            <input required className="vt-input" value={form.name} onChange={upd("name")} data-testid="bike-form-name" />
          </div>
          <div>
            <label className="overline text-neutral-600 block mb-1">Brand</label>
            <input className="vt-input" placeholder="Trek / Cube / …" value={form.brand} onChange={upd("brand")} data-testid="bike-form-brand" />
          </div>
          <div>
            <label className="overline text-neutral-600 block mb-1">Category</label>
            <select className="vt-input" value={form.category} onChange={upd("category")} data-testid="bike-form-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="overline text-neutral-600 block mb-1">Short description</label>
            <input className="vt-input" value={form.short_description} onChange={upd("short_description")} placeholder="One-line summary" data-testid="bike-form-short" />
          </div>
          <div className="md:col-span-2">
            <label className="overline text-neutral-600 block mb-1">Full description</label>
            <textarea className="vt-input" rows="3" value={form.description} onChange={upd("description")} data-testid="bike-form-description" />
          </div>
        </div>

        {/* Images */}
        <div className="mb-4">
          <label className="overline text-neutral-600 block mb-2">Images ({form.images?.length || 0})</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {form.images?.map((img, idx) => (
              <div key={idx} className="relative group">
                <img src={img} alt="" className="w-20 h-20 object-cover border border-neutral-200" />
                <button type="button" onClick={() => removeImage(idx)} className="absolute -top-1 -right-1 bg-black text-white w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${idx}`}>
                  <X size={10} />
                </button>
                {idx === 0 && <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[8px] uppercase text-center py-0.5">MAIN</span>}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => fileInput.current?.click()} disabled={uploading}
              className="text-xs border border-neutral-300 hover:border-black px-3 py-2 uppercase font-bold tracking-widest" data-testid="upload-image-btn">
              <UploadIcon size={12} className="inline mr-1" /> {uploading ? "Uploading…" : "Upload from computer"}
            </button>
            <input ref={fileInput} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadImage(e.target.files)} />
            <button type="button" onClick={addImageUrl}
              className="text-xs border border-neutral-300 hover:border-black px-3 py-2 uppercase font-bold tracking-widest">
              + Add URL
            </button>
          </div>
        </div>

        {/* Pricing */}
        <div className="mb-4">
          <label className="overline text-neutral-600 block mb-2">Rental Pricing (€)</label>
          <div className="grid grid-cols-3 gap-2">
            {PRICE_KEYS.map((k) => (
              <div key={k}>
                <label className="text-[10px] uppercase text-neutral-500 mb-1 block">{k}</label>
                <input type="number" min="0" step="0.5" className="vt-input" value={form.pricing[k] ?? 0}
                  onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, [k]: Number(e.target.value) } })}
                  data-testid={`price-${k}`} />
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="overline text-neutral-600 block mb-1">Default unit</label>
            <select className="vt-input" value={form.price_unit} onChange={upd("price_unit")}>
              {PRICE_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
              <option value="custom">custom</option>
            </select>
          </div>
          <div>
            <label className="overline text-neutral-600 block mb-1">Deposit (€)</label>
            <input type="number" min="0" step="10" className="vt-input" value={form.deposit} onChange={upd("deposit")} data-testid="bike-form-deposit" />
          </div>
          <div>
            <label className="overline text-neutral-600 block mb-1">Availability</label>
            <select className="vt-input" value={form.availability} onChange={upd("availability")}>
              {AVAIL.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="overline text-neutral-600 block mb-1">Sizes (comma-separated)</label>
          <input className="vt-input" value={form.sizes.join(", ")}
            onChange={(e) => setForm({ ...form, sizes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
            placeholder="S, M, L, XL" data-testid="bike-form-sizes" />
        </div>

        <div className="mb-4">
          <label className="overline text-neutral-600 block mb-1">Specifications (key: value on each line)</label>
          <textarea className="vt-input" rows="4"
            value={Object.entries(form.specifications || {}).map(([k, v]) => `${k}: ${v}`).join("\n")}
            onChange={(e) => {
              const specs = {};
              e.target.value.split("\n").forEach((line) => {
                const idx = line.indexOf(":");
                if (idx > 0) { const k = line.slice(0, idx).trim(); const v = line.slice(idx + 1).trim(); if (k) specs[k] = v; }
              });
              setForm({ ...form, specifications: specs });
            }}
            placeholder="Frame: Aluminum&#10;Groupset: Shimano 105" />
        </div>

        <div className="flex gap-4 mb-4 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="bike-form-active" />
            Active (visible on public site)
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="bike-form-featured" />
            Featured
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-200">
          <button type="button" onClick={onCancel} className="vt-btn-outline text-sm" disabled={saving}>Cancel</button>
          <button type="submit" className="vt-btn-orange text-sm" disabled={saving} data-testid="bike-form-save">
            {saving ? "Saving…" : (isNew ? "Create" : "Save changes")}
          </button>
        </div>
      </form>
    </div>
  );
}
